import React, { useState, useEffect } from "react";
import { Form, Input, Button } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTheme } from "../../store/context/theme-context";
import { useNotification } from "../../store/context/notification-context";
import { useApiRequests } from "../../utils/api/https/executor";
import { getSessionData } from "../../utils/interceptors/localStorageService";
import { SelfUserClassificationEnum, OtpVerificationMethodEnum } from "../../shared/constants/constants";
import { useTenant } from "../../store/context/tenant-context";
import { setAdminLogin } from "../../store/redux/actions/userActions";
import TwoFactorMethodSelector from "./TwoFactorMethodSelector";
import TwoFactorSetup from "./TwoFactorSetup";

const AdminLogin = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    loginAdmin,
    // other functions...
  } = useApiRequests();
  //const location = useLocation();
  //const isAdmin = location.pathname.startsWith("/admin");

  const { theme } = useTheme();
  const { triggerNotification } = useNotification();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showMethodSelector, setShowMethodSelector] = useState(false);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [selectedVerificationMethod, setSelectedVerificationMethod] = useState(null);
  const [setupData, setSetupData] = useState({
    qrCodeUrl: "",
    secretKey: "",
  });
  const [testMode, setTestMode] = useState(false); // For testing setup flow

  const isDisabled = formData.email === "" || formData.password === "";
  const tenant = useTenant();

  const handleInitialSubmit = async () => {
    // Prevent double submission
    if (isLoading) {
      console.log('⚠️ Login already in progress, ignoring duplicate submission');
      return;
    }
    
    // Show the 2FA method selector modal
    setShowMethodSelector(true);
  };

  // Test function to simulate first-time authenticator setup
  const handleTestSetupFlow = () => {
    // Simulate API response for first-time setup
    const mockSetupData = {
      qrCodeUrl: "otpauth://totp/YourApp:admin@example.com?secret=JBSWY3DPEHPK3PXP&issuer=YourApp",
      secretKey: "JBSWY3DPEHPK3PXP",
    };

    setSelectedVerificationMethod(OtpVerificationMethodEnum.AUTHENTICATOR_APP.value);
    setSetupData(mockSetupData);
    setShowTwoFactorSetup(true); // Fixed: was false
    setTestMode(true); // Fixed: was false

    triggerNotification({
      type: "info",
      message: "Test Mode",
      description: "Simulating first-time 2FA setup flow. Use code from authenticator app.",
    });
  };

  // Test function to simulate authenticator app NOT setup (goes to OTP page)
  const handleTestAlreadySetup = () => {
    // Simulate API response when authenticator app is already configured
    setTestMode(true);
    
    triggerNotification({
      type: "info",
      message: "Test Mode",
      description: "Simulating user with Authenticator App already configured.",
    });

    // Simulate successful login response (user already has authenticator app)
    const loginContext = JSON.stringify({
      userId: "test@example.com",
      emailAddress: "test@example.com",
      verificationMethodType: OtpVerificationMethodEnum.AUTHENTICATOR_APP.value,
      otpVerificationRequestId: "test-request-id-already-setup",
      requiresSetup: false, // Key difference: already set up
      isAdmin: true,
    });

    // Navigate directly to OTP page
    navigate("/authentication/otp", {
      state: { loginContext },
    });
  };

  const handleMethodSelected = async (verificationMethodType) => {
    try {
      setIsLoading(true);
      setSelectedVerificationMethod(verificationMethodType);

      const dataToPost = {
        userId: formData.email,
        password: formData.password,
        otpVerificationRequestId: "",
        verificationMethodType: verificationMethodType,
      };

      const response = await loginAdmin(dataToPost);
      const result = response?.outData;

      if (result.isSuccessful) {
        const data = result.data;

        // Check if user needs to setup 2FA (especially for Authenticator App)
        if (
          data.requiresSetup &&
          verificationMethodType ===
            OtpVerificationMethodEnum.AUTHENTICATOR_APP.value
        ) {
          // User hasn't set up authenticator app yet
          setSetupData({
            qrCodeUrl: data.qrCodeUrl || data.provisioningUri || "",
            secretKey: data.secretKey || data.manualEntryKey || "",
          });
          setShowMethodSelector(false);
          setShowTwoFactorSetup(true);
        } else {
          // User has already set up 2FA or using Email/SMS
          const loginContext = JSON.stringify({
            userId: dataToPost.userId,
            emailAddress: formData.email,
            verificationMethodType: verificationMethodType,
            isAdmin: true,
            ...data,
          });

          setShowMethodSelector(false);

          navigate("/authentication/otp", {
            state: { loginContext },
          });
        }
      } else {
        setShowMethodSelector(false);
        triggerNotification({
          type: "error",
          message: "Error",
          description:
            result?.message || "Failed to login. Please try again later.",
        });
      }
    } catch (error) {
      setShowMethodSelector(false);
      // Error handling
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetupVerification = async (verificationCode) => {
    try {
      setIsLoading(true);

      // If in test mode, simulate successful verification
      if (testMode) {
        setTimeout(() => {
          triggerNotification({
            type: "success",
            message: "Success",
            description: "Test mode: Setup verified successfully! Proceeding to OTP page.",
          });

          const loginContext = JSON.stringify({
            userId: "test@example.com",
            emailAddress: "test@example.com",
            verificationMethodType: selectedVerificationMethod,
            otpVerificationRequestId: "test-request-id",
            isAdmin: true,
          });

          setShowTwoFactorSetup(false);
          setTestMode(false); // Exit test mode

          navigate("/authentication/otp", {
            state: { loginContext },
          });

          setIsLoading(false);
        }, 1000);
        return;
      }

      // Verify the setup code
      const verifyResponse = await loginAdmin({
        userId: formData.email,
        password: formData.password,
        otpVerificationRequestId: "",
        verificationMethodType: selectedVerificationMethod,
        setupVerificationCode: verificationCode,
      });

      const result = verifyResponse?.outData;

      if (result.isSuccessful) {
        setShowTwoFactorSetup(false);

        // Check if backend returned full login response (setup complete)
        if (result.data?.accessToken) {
          // Complete login - backend has already authenticated the user
          const isAdminUser =
            result.data.userClassification ===
            SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value;

          dispatch(setAdminLogin(result.data));

          triggerNotification({
            type: "success",
            message: "Success",
            description: "Authenticator app setup complete! Login successful.",
          });

          // Navigate to appropriate dashboard
          navigate(isAdminUser ? "/admin-dashboard" : "/dashboard");
        } else {
          // Setup successful but needs OTP verification, proceed to OTP page
          const loginContext = JSON.stringify({
            userId: formData.email,
            emailAddress: formData.email,
            verificationMethodType: selectedVerificationMethod,
            isAdmin: true,
            ...result.data,
          });

          navigate("/authentication/otp", {
            state: { loginContext },
          });
        }
      } else {
        triggerNotification({
          type: "error",
          message: "Error",
          description:
            result?.message || "Invalid verification code. Please try again.",
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: "Failed to verify setup code. Please try again.",
      });
    } finally {
      if (!testMode) {
        setIsLoading(false);
      }
    }
  };

  const handleSubmit = handleInitialSubmit;

  useEffect(() => {
    const sessionData = getSessionData();
    if (sessionData) {
      sessionData?.userClassification ===
      SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value
        ? navigate("/admin-dashboard")
        : navigate("/dashboard");
    }
  }, [navigate]);

  return (
    <div className="p-4">
      <div className="mt-16 mb-10">
        <p className="mb-2 text-2xl font-semibold text-left">Hello Admin,</p>
        <p className="text-[#929497] text-left">
          Welcome to the Admin Portal. Please sign in to manage your account and
          access administrative features. Ensure your credentials are secure and
          do not share them with anyone.
        </p>
      </div>
      <Form
        form={form}
        layout="vertical"
        onFinishFailed={(event) => console.log(event)}
        onFinish={handleSubmit}
      >
        <div className="grid grid-cols-1 gap-1">
          <Form.Item
            label="Email Address"
            name="email"
            rules={[
              {
                required: true,
                message: "Please enter your email address.",
              },
            ]}
          >
            <Input
              placeholder="Enter your email address"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
            />
          </Form.Item>
          <Form.Item
            label="Password"
            name="password"
            rules={[
              {
                required: true,
                message: "Please enter your password.",
              },
            ]}
          >
            <Input.Password
              placeholder="Enter your password"
              iconRender={(visible) =>
                visible ? (
                  <EyeOutlined style={{ color: tenant.theme.colorPrimary }} />
                ) : (
                  <EyeInvisibleOutlined />
                )
              }
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
            />
          </Form.Item>
        </div>
        <div className="mb-5">
          <button
            type="button"
            onClick={() => navigate("/authentication/forgot-password")}
            className={` ${
              theme === "dark" ? "hover:bg-stone-800" : "hover:bg-red-50"
            }  flex hover:cursor-pointer items-center h-[40px] w-[180px] bg-transparentborder border-transparent rounded-lg`}
          >
            <span
              className="text-[16px] ml-2"
              style={{ color: tenant.theme.colorPrimary }}
            >
              Forgot your password?
            </span>
          </button>
        </div>

        <div className="flex flex-col gap-2 mt-4 text-left md:flex-row md:justify-start">
          <Button
            type="primary"
            className="w-full h-full px-3 py-2 text-base text-center shadow-none"
            htmlType="submit"
            disabled={isDisabled || isLoading}
            loading={isLoading}
          >
            Login
          </Button>
          {/* {!isAdmin && (
            <Button
              className="w-full h-full px-3 py-2 text-center text-baseshadow-none"
              htmlType="button"
              onClick={handleRegister}
            >
              Register
            </Button>
          )} */}
        </div>

        {/* Test/Demo Button - Only visible in development */}
        {false && ( //process.env.NODE_ENV === 'development'
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-3">Developer Tools:</p>
            <div className="flex flex-col gap-2">
              <Button
                type="default"
                className="w-full h-full px-3 py-2 text-sm text-center shadow-none"
                htmlType="button"
                onClick={handleTestSetupFlow}
                icon={<EyeOutlined />}
              >
                Test: First-Time Setup (Not Configured)
              </Button>
              <Button
                type="default"
                className="w-full h-full px-3 py-2 text-sm text-center shadow-none"
                htmlType="button"
                onClick={handleTestAlreadySetup}
                icon={<EyeOutlined />}
                style={{ borderColor: '#52c41a', color: '#52c41a' }}
              >
                Test: Already Configured (Skip Setup)
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-2 italic">
              Use authenticator code: Works with real apps or any 6-digit code in test mode
            </p>
          </div>
        )}
      </Form>

      {/* 2FA Method Selection Modal */}
      <TwoFactorMethodSelector
        visible={showMethodSelector}
        onSelect={handleMethodSelected}
        onCancel={() => setShowMethodSelector(false)}
        loading={isLoading}
      />

      {/* 2FA Setup Modal (for Authenticator App) */}
      <TwoFactorSetup
        visible={showTwoFactorSetup}
        qrCodeUrl={setupData.qrCodeUrl}
        secretKey={setupData.secretKey}
        onVerify={handleSetupVerification}
        onCancel={() => {
          setShowTwoFactorSetup(false);
          setShowMethodSelector(true); // Go back to method selection
        }}
        loading={isLoading}
      />
    </div>
  );
};

export default AdminLogin;
